import React from 'react';
import { X, Database } from 'lucide-react';
import type { EvidenceAssertionRow } from './AssertionList';

export function AssertionDetailDrawer(props: {
  assertion: EvidenceAssertionRow | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectLineageRef: (refId: string) => void;
}): JSX.Element | null {
  const { assertion, isOpen, onClose, onSelectLineageRef } = props;
  if (!isOpen || !assertion) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-[60]" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-[61] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900">Assertion</h3>
            <div className="text-xs text-gray-500 font-mono truncate">{assertion.metricKey}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="font-semibold text-gray-900">{assertion.label}</div>
            <div className="mt-1 text-sm text-gray-700">
              {assertion.dataStatus === 'known' ? (
                typeof assertion.value === 'object' && assertion.value !== null ? (
                  <pre className="font-mono text-xs bg-gray-50 rounded p-2 overflow-x-auto max-h-40">
                    {JSON.stringify(assertion.value, null, 2)}
                  </pre>
                ) : (
                  <>
                    <span className="font-mono">{String(assertion.value)}</span>
                    {assertion.unit ? <span className="text-gray-500"> {assertion.unit}</span> : null}
                  </>
                )
              ) : (
                <span className="text-gray-500 italic">Unavailable</span>
              )}
            </div>
            <div className="mt-2 text-xs text-gray-600">
              Status:{' '}
              <span className={assertion.dataStatus === 'known' ? 'text-green-700 font-semibold' : 'text-gray-600 font-semibold'}>
                {assertion.dataStatus === 'known' ? 'Known' : 'Unavailable'}
              </span>
            </div>
            {assertion.notes?.length ? (
              <div className="mt-3 text-xs text-gray-700">
                <div className="font-medium text-gray-800 mb-1">Notes</div>
                <ul className="list-disc ml-5 space-y-1">
                  {assertion.notes.slice(0, 6).map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-800 flex items-center gap-2">
              <Database className="w-4 h-4 text-blue-600" />
              Lineage References
            </div>
            <div className="divide-y divide-gray-100">
              {assertion.lineageRefIds?.length ? (
                assertion.lineageRefIds.map((ref) => (
                  <button
                    key={ref}
                    onClick={() => onSelectLineageRef(ref)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors"
                  >
                    <div className="text-xs font-mono text-gray-900 break-all">{ref}</div>
                    <div className="text-xs text-gray-500">Click to view tool call details</div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-600">No lineage references.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}


